import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  Divider,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExportMenuItems,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  POSITION,
  Toggle,
  columnTypes,
  parseDate,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterGroupingToggling,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useSecuritiesContext,
  useWSFilterPipe,
  useWsBlotterTable,
  type BlotterGroupingDefinition,
  type Leaves,
  type Position,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';

import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import { BLOTTER_TABLE_FILTERS_CONTAINER_ID, DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS } from '@talos/kyoko';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import { useFeatureFlag } from '../../../../hooks';
import { defaultDerivativesBlotterColumns, defaultDerivativesBlotterFilter } from '../defaults';
import { GROUP_COL_WIDTH, POSITIONS_BLOTTER_CONVERSION_TOLERANCE } from '../tokens';
import type {
  ClonePositionsTabFunc,
  DerivativesPositionsTabProps,
  ExtraPositionColumns,
  PositionsTableFilter,
} from '../types';
import { usePositionsMenu } from '../usePositionsMenu';
import { useShowAdjustMarketAccountPositionButton } from '../useShowAdjustMarketAccountPositionButton';
import { usePositionsDerivativesColumns } from './usePositionsDerivativesColumns';
import { DERIVATIVES_PRODUCT_TYPES, usePositionsDerivativesFilter } from './usePositionsDerivativesFilter';

export interface PositionsDerivativesBlotterParams {
  blotterID: string;
  initialIsOpen?: boolean;
  tab: DerivativesPositionsTabProps;
  onUpdateTab: (tab: DerivativesPositionsTabProps) => void;
  onCloneTab: ClonePositionsTabFunc;
  tag?: string;
}

function getPositionKey(position: Position) {
  return position.rowID;
}

const derivAggSpecs = [
  { valuePath: 'Equivalent.Delta', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.UnrealizedPnL', currencyPath: 'Equivalent.Currency' },
] satisfies { valuePath: Leaves<Position>; currencyPath: Leaves<Position> }[];

type DerivsGroupings = 'Underlying' | 'Product' | 'Expiry' | 'Market Account';
type GroupablePositionColumns = keyof Position | ExtraPositionColumns;
const GROUPINGS: BlotterGroupingDefinition<DerivsGroupings, GroupablePositionColumns>[] = [
  {
    key: 'Underlying',
    label: 'By Underlying',
    rowGroupColumns: ['underlying', 'AssetType'],
    mixpanelEvent: MixpanelEvent.GroupAssets,
    buttonDataTestId: 'by-asset-button',
  },
  {
    key: 'Market Account',
    label: 'By Market Account',
    rowGroupColumns: ['MarketAccount'],
    mixpanelEvent: MixpanelEvent.GroupAccounts,
    buttonDataTestId: 'by-market-account-button',
  },
  {
    key: 'Product',
    label: 'By Product',
    rowGroupColumns: ['AssetType', 'underlying'],
    mixpanelEvent: MixpanelEvent.GroupProducts,
    buttonDataTestId: 'by-product-button',
  },
  {
    key: 'Expiry',
    label: 'By Expiry',
    rowGroupColumns: ['expiry', 'underlying'],
    mixpanelEvent: MixpanelEvent.GroupExpiries,
    buttonDataTestId: 'by-expiry-button',
  },
];

export function PositionsDerivativesBlotter({
  blotterID,
  tab,
  onUpdateTab,
  initialIsOpen,
  onCloneTab,
  tag,
}: PositionsDerivativesBlotterParams) {
  const mixpanel = useMixpanel();
  const { enableLegacyUpdateBalances } = useFeatureFlag();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultDerivativesBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultDerivativesBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;

  const { securitiesBySymbol } = useSecuritiesContext();

  const defaultColumns = usePositionsDerivativesColumns(defaultBlotterColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
    extraGridState: {
      rowGroupExpansion: {
        expansionState: 'all-expanded',
        expansionExceptions: [],
      },
    },
  });

  const filterResults = usePositionsDerivativesFilter({
    persistedBlotterTable,
    defaultFilter,
  });

  const { filterBuilderProps } = filterResults;

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const { homeCurrency } = useDisplaySettings();
  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const positionsMenu = usePositionsMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    tabType: 'Derivatives',
    showOpenTransactionsMenuItem: true,
  });

  const columnsWithMenu = useMemo(
    () => [...defaultColumns, ...positionsMenu.columns],
    [defaultColumns, positionsMenu.columns]
  );

  const aggPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getPositionKey,
    aggSpecs: derivAggSpecs,
  });

  const rxjsFilterFunc = useCallback(
    (position: Position) => {
      // This filtering function just checks the expiry property.
      if (filterResults.filter.Expiries != null) {
        // This resolving will be removed once the Position entity has expiration on it. Backend will add.
        const expiration = securitiesBySymbol.get(position.Asset)?.Expiration;
        if (expiration == null) {
          // We have an expiration filter applied, but the security of this position has no expiry. Exclude it.
          return false;
        }

        const expirationDate = parseDate(expiration);
        const isAllowed = filterResults.filter.Expiries?.some(expiryString => {
          const allowedExpiryDate = parseDate(expiryString);
          return isSameDay(expirationDate, allowedExpiryDate);
        });
        if (!isAllowed) {
          return false;
        }
      }

      return true;
    },
    [securitiesBySymbol, filterResults.filter]
  );

  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionKey, filterFunc: rxjsFilterFunc });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        width: GROUP_COL_WIDTH,
        hide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const baseRequest = useMemo(
    () => ({
      name: POSITION,
      tag: blotterID,
      ShowZeroBalances: showZeroBalances,
      EquivalentCurrency: homeCurrency,
      Tolerance: POSITIONS_BLOTTER_CONVERSION_TOLERANCE,
    }),
    [showZeroBalances, homeCurrency, blotterID]
  );

  const request = useMemo(
    () => ({
      ...baseRequest,
      ...removeEmptyFilters(onlyServerKeys(filterResults.filter)),
    }),
    [baseRequest, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    pipe: filterPipe,
    pinnedRowDataPipe: aggPipe,
    rowID: 'rowID' satisfies keyof Position,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems: positionsMenu.getContextMenuItems,
      showOpenedGroup: true,
      autoGroupColumnDef,
      groupDisplayType: 'singleColumn',
    },
    exportOptions: {
      name: 'PositionsDerivatives',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns } = blotterTable;

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'positions derivatives',
    setRowGroupColumns,
    groupings: GROUPINGS,
    allowNoGrouping: true,
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const showAdjustMarketAccountPositionButton = useShowAdjustMarketAccountPositionButton();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={groupingToggleButtons}
        portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
            />
          </>
        }
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                tooltip={CONTROL_TOOLTIPS.showZeroBalances}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              <Divider orientation="horizontal" />
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
              {showAdjustMarketAccountPositionButton && (
                <Button
                  startIcon={IconName.Pencil}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  data-testid="update-balances-button"
                  onClick={() => {
                    positionsMenu.openUpdateBalancesDialog();
                  }}
                >
                  {enableLegacyUpdateBalances ? 'Update Balances' : 'Adjust Market Account Position'}
                </Button>
              )}
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {positionsMenu.dialogs}
    </>
  );
}

function onlyServerKeys(filter: PositionsTableFilter | undefined): PositionsTableFilter {
  if (filter == null) {
    return {};
  }

  const hasAssetTypeFilter = filter.AssetTypes != null && filter.AssetTypes.length > 0;

  return {
    ...filter,
    Expiries: undefined,
    // If the user has deselected all product type filters, that means we load all of them, so set that backend filter for AssetTypes
    AssetTypes: hasAssetTypeFilter ? filter.AssetTypes : DERIVATIVES_PRODUCT_TYPES,
  };
}
